if (!customElements.get('product-modal')) {
  const galleryLabels = {
    es: ['Cerrar', 'Ampliar / reducir', 'Anterior', 'Siguiente', 'Imagen', 'No se ha podido cargar la imagen.', 'Desliza para ver más'],
    en: ['Close', 'Zoom in / out', 'Previous', 'Next', 'Image', 'The image could not be loaded.', 'Swipe to see more'],
    de: ['Schlie\u00dfen', 'Vergr\u00f6\u00dfern / verkleinern', 'Zur\u00fcck', 'Weiter', 'Bild', 'Das Bild konnte nicht geladen werden.', 'Wischen für weitere Bilder'],
    fr: ['Fermer', 'Agrandir / r\u00e9duire', 'Pr\u00e9c\u00e9dent', 'Suivant', 'Image', "Impossible de charger l'image.", 'Faites glisser pour voir plus'],
    it: ['Chiudi', 'Ingrandisci / riduci', 'Precedente', 'Successiva', 'Immagine', "Impossibile caricare l'immagine.", 'Scorri per vedere altro'],
    pt: ['Fechar', 'Ampliar / reduzir', 'Anterior', 'Seguinte', 'Imagem', 'N\u00e3o foi poss\u00edvel carregar a imagem.', 'Deslize para ver mais'],
  };

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      show(opener) {
        if (this.hasAttribute('open')) return;
        this.wasScrollLocked = document.body.classList.contains('overflow-hidden');
        this.openVersion = (this.openVersion || 0) + 1;
        super.show(opener);
        const activeMedia = this.showActiveMedia();
        if (activeMedia?.tagName === 'IMG' && this.dataset.galleryModule) {
          this.openGallery(activeMedia, this.openVersion);
        }
      }

      hide() {
        this.openVersion = (this.openVersion || 0) + 1;
        if (this.gallery) this.gallery.close();
        else this.finishClose();
      }

      finishClose() {
        if (!this.hasAttribute('open')) return;
        delete this.dataset.galleryActive;
        const opener = this.openedBy;
        // Restore focus without scrolling the product underneath the closing viewer.
        this.openedBy = null;
        super.hide();
        this.openedBy = opener;
        if (this.wasScrollLocked) document.body.classList.add('overflow-hidden');
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      }

      disconnectedCallback() {
        this.openVersion = (this.openVersion || 0) + 1;
        if (this.gallery) this.gallery.destroy();
        this.finishClose();
      }

      showActiveMedia() {
        const items = [...this.querySelectorAll('.product-media-modal__content > [data-media-id]')];
        const activeMedia = items.find((item) => item.dataset.mediaId === this.openedBy?.dataset.mediaId) || items[0];
        items.forEach((item) => item.classList.toggle('active', item === activeMedia));
        const container = this.querySelector('[role="document"]');
        container.scrollTop = 0;
        container.scrollLeft = 0;
        if (activeMedia?.nodeName === 'DEFERRED-MEDIA' && activeMedia.querySelector('template')?.content.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
        return activeMedia;
      }

      async openGallery(activeMedia, version) {
        try {
          const { default: PhotoSwipe } = await import(this.dataset.galleryModule);
          if (!this.isConnected || !this.hasAttribute('open') || version !== this.openVersion) return;

          // Variant changes replace this content; always build from the current DOM.
          const images = [...this.querySelectorAll('.product-media-modal__content > img[data-media-id]')]
            .filter((image) => !image.classList.contains('product__media-item--variant') || image === image.parentElement.firstElementChild || image === activeMedia);
          const dataSource = images.map((image) => ({
            src: image.dataset.gallerySrc || image.src,
            width: Number(image.dataset.galleryWidth) || image.naturalWidth,
            height: Number(image.dataset.galleryHeight) || image.naturalHeight,
            alt: image.alt,
            thumbnail: image.dataset.galleryThumbnail || image.src,
          }));
          const index = images.indexOf(activeMedia);
          if (index < 0 || dataSource.some((image) => !image.width || !image.height)) return;
          const labels = galleryLabels[document.documentElement.lang.toLowerCase().split('-')[0]] || galleryLabels.en;
          const multiple = images.length > 1;
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const gallery = new PhotoSwipe({
            dataSource, index,
            mainClass: 'product-gallery',
            bgOpacity: 1,
            showHideAnimationType: reducedMotion ? 'none' : 'fade',
            showAnimationDuration: 180,
            hideAnimationDuration: 150,
            returnFocus: false,
            imageClickAction: 'zoom',
            tapAction: false,
            arrowPrev: multiple,
            arrowNext: multiple,
            counter: multiple,
            closeTitle: labels[0],
            zoomTitle: labels[1],
            arrowPrevTitle: labels[2],
            arrowNextTitle: labels[3],
            errorMsg: labels[5],
            paddingFn: (viewport) => {
              const style = getComputedStyle(this);
              const inset = (side) => parseFloat(style.getPropertyValue('--gallery-inset-' + side)) || 0;
              return {
                top: 72 + inset('top'),
                bottom: (multiple ? 104 : 24) + inset('bottom'),
                left: (viewport.x >= 750 ? 72 : 12) + inset('left'),
                right: (viewport.x >= 750 ? 72 : 12) + inset('right'),
              };
            },
          });
          this.gallery = gallery;
          gallery.on('keydown', ({ originalEvent: event }) => {
            if (event.key !== 'Tab') return;
            const controls = [...gallery.element.querySelectorAll('button:not([disabled])')]
              .filter((button) => button.getClientRects().length && getComputedStyle(button).visibility !== 'hidden');
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && (document.activeElement === first || document.activeElement === gallery.element)) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          });
          gallery.on('uiRegister', () => {
            if (!multiple) return;
            gallery.ui.registerElement({
              name: 'thumbnails',
              appendTo: 'root',
              onInit: (rail) => {
                rail.setAttribute('role', 'group');
                rail.setAttribute('aria-label', this.querySelector('[role="dialog"]').getAttribute('aria-label'));
                const buttons = dataSource.map((item, i) => {
                  const button = document.createElement('button');
                  button.type = 'button';
                  button.className = 'product-gallery__thumbnail';
                  button.setAttribute('aria-label', labels[4] + ' ' + (i + 1));
                  button.title = labels[4] + ' ' + (i + 1);
                  const image = document.createElement('img');
                  image.src = item.thumbnail;
                  image.alt = '';
                  image.width = 56;
                  image.height = 56;
                  image.loading = 'lazy';
                  button.append(image);
                  button.addEventListener('click', () => gallery.goTo(i));
                  rail.append(button);
                  return button;
                });
                gallery.on('change', () => {
                  buttons.forEach((button, i) => button.setAttribute('aria-current', String(i === gallery.currIndex)));
                  const current = buttons[gallery.currIndex];
                  rail.scrollLeft = current.offsetLeft - (rail.clientWidth - current.clientWidth) / 2;
                });
              },
            });
            gallery.ui.registerElement({
              name: 'swipeHint',
              appendTo: 'root',
              onInit: (hint) => {
                hint.className = 'product-gallery__swipe-hint';
                hint.textContent = labels[6];
                hint.setAttribute('aria-hidden', 'true');
              },
            });
          });
          gallery.on('afterInit', () => {
            gallery.element.setAttribute('aria-label', this.querySelector('[role="dialog"]').getAttribute('aria-label'));
            const counter = gallery.element.querySelector('.pswp__counter');
            if (counter) {
              counter.setAttribute('role', 'status');
              counter.setAttribute('aria-live', 'polite');
            }
          });
          gallery.on('destroy', () => {
            if (this.gallery !== gallery) return;
            this.gallery = null;
            this.finishClose();
          });
          removeTrapFocus();
          this.dataset.galleryActive = 'true';
          gallery.init();
          const swipeHint = gallery.element.querySelector('.product-gallery__swipe-hint');
          if (swipeHint) gallery.on('change', () => swipeHint.classList.add('is-dismissed'));
        } catch (error) {
          // Keep the native modal usable if the optional gallery cannot load.
          const gallery = this.gallery;
          this.gallery = null;
          if (gallery) gallery.destroy();
          delete this.dataset.galleryActive;
          if (this.hasAttribute('open')) trapFocus(this, this.querySelector('[role="dialog"]'));
        }
      }
    }
  );
}
